/**
 * Profile Completion Utilities
 * 
 * Determines if a reviewer profile is "complete" for public discovery.
 * A complete profile requires (5 requirements):
 * - Profile Photo (valid avatar URL)
 * - Username (required)
 * - Bio (80-500 characters)
 * - Stripe Connected (to get paid)
 * - Gone Live 30+ min (proves engagement)
 * 
 * NOTE: Display Name removed (auto-filled at signup, not meaningful)
 * 
 * Incomplete profiles are hidden from public discovery but can still:
 * - Log in
 * - Access dashboard
 * - Manage sessions
 */

/**
 * Minimum bio length required for profile completion
 */
export const MIN_BIO_LENGTH = 80;

/**
 * Maximum bio length allowed
 */
export const MAX_BIO_LENGTH = 500;

/**
 * Check if a user has a valid avatar
 * @param {Object} user - User object
 * @returns {boolean} True if user has a valid avatar
 */
export const hasValidAvatar = (user) => {
  if (!user) return false;
  
  const avatarUrl = user.avatarUrl || user.avatar;
  
  // No avatar URL
  if (!avatarUrl || avatarUrl === 'null' || avatarUrl === 'undefined' || avatarUrl === '') {
    return false;
  }
  
  // Reject legacy paths that don't persist
  if (avatarUrl.includes('/api/uploads/avatars/')) {
    return false;
  }
  
  // Has a valid avatar (GridFS or external URL)
  return true;
};

/**
 * Check if a user has a valid display name
 * @param {Object} user - User object
 * @returns {boolean} True if user has a valid display name
 */
export const hasValidDisplayName = (user) => {
  if (!user) return false;
  
  const name = user.name || user.displayName;
  return Boolean(name && name.trim().length > 0);
};

/**
 * Check if a user has a valid username
 * @param {Object} user - User object
 * @returns {boolean} True if user has a valid username
 */
export const hasValidUsername = (user) => {
  if (!user) return false;
  
  const username = user.username;
  return Boolean(username && username.trim().length > 0);
};

/**
 * Check if a user has a valid bio (80-500 chars)
 * @param {Object} user - User object
 * @returns {boolean} True if user has a valid bio
 */
export const hasValidBio = (user) => {
  if (!user) return false;
  
  const bio = user.bio;
  if (!bio || typeof bio !== 'string') return false;
  
  const trimmedBio = bio.trim();
  return trimmedBio.length >= MIN_BIO_LENGTH && trimmedBio.length <= MAX_BIO_LENGTH;
};

/**
 * Get the current bio length
 * @param {Object} user - User object
 * @returns {number} Bio length or 0
 */
export const getBioLength = (user) => {
  if (!user || !user.bio) return 0;
  return user.bio.trim().length;
};

/**
 * Check if a user has Stripe connected AND completed (required for payments)
 * @param {Object} user - User object
 * @returns {boolean} True if user has completed Stripe onboarding
 */
export const hasStripeConnected = (user) => {
  if (!user) return false;
  
  // Must have stripe_details_submitted === true to be considered complete
  // Just having stripe_account_id means they started but didn't finish
  return user.stripe_details_submitted === true;
};

/**
 * Check if a user has started their first session (requires Stripe)
 * @param {Object} user - User object
 * @returns {boolean} True if user has Stripe AND has started at least 1 session
 */
export const hasStartedFirstSession = (user) => {
  if (!user) return false;
  
  // Must have Stripe connected AND at least 1 session
  // Sessions started before Stripe don't count for visibility
  const hasStripe = user.stripe_details_submitted === true;
  const totalSessions = user.total_sessions || user.totalSessions || 0;
  return hasStripe && totalSessions > 0;
};

/**
 * Check if a reviewer profile is complete
 * @param {Object} user - User/reviewer object
 * @returns {boolean} True if profile is complete
 */
export const isProfileComplete = (user) => {
  if (!user) return false;
  
  // 5 requirements: Photo, Username, Bio, Stripe, First Session
  return (
    hasValidAvatar(user) &&
    hasValidUsername(user) &&
    hasValidBio(user) &&
    hasStripeConnected(user) &&
    hasStartedFirstSession(user)
  );
};

/**
 * Check if user can start sessions (first 4 steps complete)
 * This gates the "Start New Session" button
 * Steps: 1. Username, 2. Avatar, 3. Bio, 4. Payment method
 * @param {Object} user - User/reviewer object
 * @returns {boolean} True if user can start sessions
 */
export const canStartSessions = (user) => {
  if (!user) return false;
  
  // Check for Pro Plan with Cash App/Venmo
  const isProPlan = user.monetization_plan === 'subscription_cashapp_venmo';
  const isSubscriptionActive = user.subscription_status === 'active' || user.subscription_status === 'trialing';
  const hasCashAppOrVenmo = !!(user.cash_app || user.venmo);
  
  // Payment is complete if Stripe OR (Pro Plan + active + Cash App/Venmo)
  const hasPayment = hasStripeConnected(user) || (isProPlan && isSubscriptionActive && hasCashAppOrVenmo);
  
  // First 4 steps: Username, Avatar, Bio, Payment
  return (
    hasValidUsername(user) &&
    hasValidAvatar(user) &&
    hasValidBio(user) &&
    hasPayment
  );
};

/**
 * Get list of missing profile fields
 * @param {Object} user - User/reviewer object
 * @returns {Array<string>} Array of missing field names
 */
export const getMissingFields = (user) => {
  const missing = [];
  
  if (!hasValidAvatar(user)) {
    missing.push('avatar');
  }
  
  if (!hasValidUsername(user)) {
    missing.push('username');
  }
  
  if (!hasValidBio(user)) {
    missing.push('bio');
  }
  
  if (!hasStripeConnected(user)) {
    missing.push('stripe');
  }
  
  if (!hasStartedFirstSession(user)) {
    missing.push('firstsession');
  }
  
  return missing;
};

/**
 * Get human-readable descriptions of missing fields
 * @param {Object} user - User/reviewer object
 * @returns {Array<Object>} Array of {field, message} objects
 */
export const getMissingFieldDescriptions = (user) => {
  const descriptions = [];
  
  if (!hasValidAvatar(user)) {
    descriptions.push({
      field: 'avatar',
      message: 'Upload a profile photo (JPG, PNG, or WEBP, min 400×400)',
      section: 'bio'
    });
  }
  
  if (!hasValidUsername(user)) {
    descriptions.push({
      field: 'username',
      message: 'Set your username',
      section: 'bio'
    });
  }
  
  if (!hasValidBio(user)) {
    const bioLength = getBioLength(user);
    if (bioLength === 0) {
      descriptions.push({
        field: 'bio',
        message: `Write a bio (minimum ${MIN_BIO_LENGTH} characters)`,
        section: 'bio'
      });
    } else if (bioLength < MIN_BIO_LENGTH) {
      descriptions.push({
        field: 'bio',
        message: `Bio too short (${bioLength}/${MIN_BIO_LENGTH} characters minimum)`,
        section: 'bio'
      });
    } else if (bioLength > MAX_BIO_LENGTH) {
      descriptions.push({
        field: 'bio',
        message: `Bio too long (${bioLength}/${MAX_BIO_LENGTH} characters maximum)`,
        section: 'bio'
      });
    }
  }
  
  if (!hasStripeConnected(user)) {
    descriptions.push({
      field: 'stripe',
      message: 'Connect Stripe to get paid for reviews',
      section: 'settings'
    });
  }
  
  if (!hasStartedFirstSession(user)) {
    descriptions.push({
      field: 'firstsession',
      message: 'Start your first live session to become visible',
      section: 'live'
    });
  }
  
  return descriptions;
};

/**
 * Get profile completion percentage
 * @param {Object} user - User/reviewer object
 * @returns {number} Percentage complete (0-100)
 */
export const getCompletionPercentage = (user) => {
  if (!user) return 0;
  
  let completed = 0;
  const total = 5; // avatar, username, bio, stripe, first session
  
  if (hasValidAvatar(user)) completed++;
  if (hasValidUsername(user)) completed++;
  if (hasValidBio(user)) completed++;
  if (hasStripeConnected(user)) completed++;
  if (hasStartedFirstSession(user)) completed++;
  
  return Math.round((completed / total) * 100);
};

export default {
  MIN_BIO_LENGTH,
  MAX_BIO_LENGTH,
  hasValidAvatar,
  hasValidDisplayName,
  hasValidUsername,
  hasValidBio,
  hasStripeConnected,
  hasStartedFirstSession,
  getBioLength,
  isProfileComplete,
  getMissingFields,
  getMissingFieldDescriptions,
  getCompletionPercentage
};
